// libs
import {
  ActorProfilePreviewFragmentFragment,
  ActorProfileType,
  ProfileSortingKeys,
  SortDirection,
  useCategorizedProfiles,
  useGlobalPermissions,
  useProfiles
} from '@lopo-tech/lopo-api'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

// components
import EmptySearchResults from 'src/components/empty_search_results/empty_search_results'
import ItemGroupHeader from 'src/components/item_group_header/item_group_header'
import LoadingSpinnerWrapper from 'src/components/loading_spinner/loading_spinner_wrapper'
import { ProfilesList } from 'src/components/profiles_list/profiles_list'
import ProfilesListCategorized from 'src/components/profiles_list/profiles_list_categorized'
import SearchField from 'src/components/search_field/search_field'
import ProfileListWrapper from 'src/containers/smart_components/list_wrapper/profile_list_wrapper'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants'

// helpers
import { textResources } from 'src/lang/de'

import { useLocation } from 'react-router-dom'
import { PROFILES_LIST_PER_PAGE } from 'src/constants/profile_list'
import {
  PROFILE_LIST_BLOG_URI,
  PROFILE_LIST_PRESS_URI,
  PROFILE_LIST_PRIVATE_URI
} from 'src/constants/urls'
import { IMarker } from 'src/interfaces/marker'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { ICategorizedProfiles } from 'src/reducers/profiles_list/model'
import { calculateProfileMarkers } from 'src/utils/profile'
import { useCallToAction } from '../../../../actions/onboarding_cta/onboarding_cta_hooks'
import { useSelectIsUserLoggedIn } from '../../../../reducers/user/user_hooks'
import { getConfigValue } from '../../../../utils/config/config'
import './profiles_directory_categorized.scss'

const cls = CLASS_PREFIX + 'profiles-directory-categorized'
const searchCls = cls + '__search'

export interface IViewProps {
  onSubmit: (query: string) => void;
  query: string;
  setMarkers: (markers: IMarker[]) => void;
}

const useHideSearch = () => {
  const shouldHideSearch = !!getConfigValue<boolean>(
    'featureFlags.search.disableSearchForLoggedOut'
  )
  const callToAction = useCallToAction()
  const isUserLoggedIn = useSelectIsUserLoggedIn()

  return useCallback(
    (event, callback) => {
      if (shouldHideSearch && !isUserLoggedIn) {
        event.preventDefault()
        callback()
        callToAction()
      }
    },
    [callToAction, shouldHideSearch, isUserLoggedIn]
  )
}

const EMPTY_PROFILE_LIST: ActorProfilePreviewFragmentFragment[] = []

const useShownProfileTypes = () => {
  const {
    canShowAuthorityProfilesList,
    canShowBlogProfilesList,
    canShowPressProfilesList,
    canShowPrivateProfilesList
  } = useGlobalPermissions()

  const shownProfileTypes: ActorProfileType[] = []

  if (canShowAuthorityProfilesList) {
    shownProfileTypes.push(ActorProfileType.AuthorityProfile)
  }
  if (canShowBlogProfilesList) {
    shownProfileTypes.push(ActorProfileType.BlogProfile)
  }
  if (canShowPressProfilesList) {
    shownProfileTypes.push(ActorProfileType.PressProfile)
  }
  if (canShowPrivateProfilesList) {
    shownProfileTypes.push(ActorProfileType.PrivateProfile)
  }

  return shownProfileTypes
}

export const CategorizedView: React.FC<IViewProps> = ({ onSubmit, query, setMarkers }) => {
  const { pathname } = useLocation()
  const locationShape = useGetAppLocationShape()

  const shownProfileTypes = useShownProfileTypes()

  const {
    data: profileCategoryData,
    loading: isLoadingProfileCategoryData
  } = useCategorizedProfiles({
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      locationShapes: [locationShape?.id!],
      perCategory: 3
    },
    errorPolicy: 'all',
    skip: !locationShape
  })

  const {
    data: profilesData,
    loading: isLoadingProfiles,
    fetchMore: fetchMoreProfiles
  } = useProfiles({
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      locationShapes: [locationShape?.id!],
      types: shownProfileTypes,
      page: 1,
      perPage: PROFILES_LIST_PER_PAGE,
      sorting: {
        direction: SortDirection.Desc,
        fieldName: ProfileSortingKeys.CreatedAt
      }
    },
    skip: !locationShape || !shownProfileTypes.length
  })
  const profiles = profilesData?.profiles
  const additionalProfiles = profiles?.items ?? EMPTY_PROFILE_LIST

  const currentPage = profiles?.pageInfo.previousPage ?? 0 + 1
  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(isLoadingProfiles)
  loadingRef.current = isLoadingProfiles
  const getNextPage = useCallback(() => {
    const nextPage = profiles?.pageInfo.nextPage
    if (nextPage && !loadingRef.current) {
      fetchMoreProfiles({ variables: { page: nextPage } })
    }
  }, [profiles?.pageInfo.nextPage, fetchMoreProfiles])

  const isPageLoading = isLoadingProfileCategoryData || isLoadingProfiles

  const pressProfiles = profileCategoryData?.pressProfiles?.items ?? EMPTY_PROFILE_LIST
  const privateProfiles = profileCategoryData?.privateProfiles?.items ?? EMPTY_PROFILE_LIST
  const publicProfiles = profileCategoryData?.publicProfiles?.items ?? EMPTY_PROFILE_LIST

  const profilesCategorized = useMemo((): ICategorizedProfiles[] => {
    const result: ICategorizedProfiles[] = []

    if (pressProfiles.length) {
      result.push({
        profiles: pressProfiles,
        title: textResources.profile.extensionPress_WEB,
        url: `${pathname}${PROFILE_LIST_PRESS_URI}`
      })
    }

    if (publicProfiles.length) {
      result.push({
        profiles: publicProfiles,
        title: textResources.profile.extensionAuthority,
        url: `${pathname}${PROFILE_LIST_BLOG_URI}`
      })
    }

    if (privateProfiles.length) {
      result.push({
        profiles: privateProfiles.filter(
          (item) => item.slug !== PROFILE_LIST_PRIVATE_URI
        ),
        title: textResources.profile.extensionPrivate,
        url: `${pathname}${PROFILE_LIST_PRIVATE_URI}`
      })
    }

    return result
  }, [pressProfiles, privateProfiles, publicProfiles])

  const emptyDirectory =
    profilesCategorized.length === 0 && additionalProfiles.length === 0

  useEffect(() => {
    const allProfiles = [
      ...pressProfiles,
      ...publicProfiles,
      ...privateProfiles
    ]
    setMarkers(calculateProfileMarkers(allProfiles))
  }, [setMarkers, pressProfiles, publicProfiles, privateProfiles])

  const hideSearch = useHideSearch()

  return (
    <div>
      <div className={cls}>
        <div className={searchCls}>
          <SearchField
            onSubmit={onSubmit}
            defaultValue={query}
            onFocus={hideSearch}
          />
          {emptyDirectory && !isPageLoading && (
            <EmptySearchResults
              subtitle={textResources.shared.noLocalResultFound}
            />
          )}
        </div>
      </div>
      <LoadingSpinnerWrapper isLoading={isPageLoading}>
        <ProfilesListCategorized categorizedProfiles={profilesCategorized} />
        {!!additionalProfiles.length && (
          <>
            <div className={`${cls}__more-title`}>
              <ItemGroupHeader
                title={textResources.shared.hereIsMore}
                subtitle={textResources.profileSearch.relatedContentSubtitle}
              />
            </div>
            {/* TODO: Check whether additional profile list necessary */}
            <ProfileListWrapper
              currentPage={currentPage}
              getNextPage={getNextPage}
              initialLoad={false}
            >
              <ProfilesList profiles={additionalProfiles} />
            </ProfileListWrapper>
          </>
        )}
      </LoadingSpinnerWrapper>
    </div>
  )
}

export const SearchView: React.FC<IViewProps> = ({ onSubmit, query, setMarkers }) => {
  const hideSearch = useHideSearch()

  const shownProfileTypes = useShownProfileTypes()

  const locationShape = useGetAppLocationShape()
  const { data, fetchMore, loading } = useProfiles({
    variables: {
      filters: { nameCont: query },
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      locationShapes: [locationShape?.id!],
      types: shownProfileTypes,
      page: 1,
      perPage: PROFILES_LIST_PER_PAGE
    },
    skip: !locationShape || !shownProfileTypes.length
  })

  const profilesList = data?.profiles?.items ?? EMPTY_PROFILE_LIST

  const currentPage = data?.profiles?.pageInfo.previousPage ?? 0 + 1
  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (data?.profiles?.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: data.profiles.pageInfo.nextPage } })
    }
  }, [fetchMore, data?.profiles?.pageInfo.nextPage])

  useEffect(() => {
    setMarkers(calculateProfileMarkers(profilesList))
  }, [profilesList, setMarkers])

  return (
    <div>
      <div className={cls}>
        <div
          className={classNames(searchCls, searchCls + '--show-search-results')}
        >
          <SearchField
            onSubmit={onSubmit}
            defaultValue={query}
            onFocus={hideSearch}
          />
          {profilesList.length === 0 && (
            <EmptySearchResults
              subtitle={textResources.shared.noLocalResultFound}
            />
          )}
        </div>
      </div>
      <LoadingSpinnerWrapper isLoading={loading}>
        <ProfileListWrapper currentPage={currentPage} getNextPage={getNextPage}>
          <ProfilesList profiles={profilesList} />
        </ProfileListWrapper>
      </LoadingSpinnerWrapper>
    </div>
  )
}
